import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { fmtZahl, fmtEuro, fmtDatum } from '../components/ZeitraumWahl';

const KURS_REIHENFOLGE = ['pirat', 'seepferdchen', 'trixi', 'bronze', 'silber', 'gold'];
const KURS_INFO = {
  pirat:        { label: 'Pirat',        kurz: 'Pir.',  farbe: '#880e4f', bg: '#fce4ec', emoji: '🏴‍☠️' },
  seepferdchen: { label: 'Seepferdchen', kurz: 'See.',  farbe: '#0d47a1', bg: '#e3f2fd', emoji: '🐴'  },
  trixi:        { label: 'Trixi',        kurz: 'Tri.',  farbe: '#1b5e20', bg: '#e8f5e9', emoji: '🌟'  },
  bronze:       { label: 'Bronze',       kurz: 'Bro.',  farbe: '#e65100', bg: '#fff3e0', emoji: '🥉'  },
  silber:       { label: 'Silber',       kurz: 'Sil.',  farbe: '#424242', bg: '#f5f5f5', emoji: '🥈'  },
  gold:         { label: 'Gold',         kurz: 'Gold',  farbe: '#f57f17', bg: '#fffde7', emoji: '🥇'  },
};

// Alle Zeitraum-Grenzen als hiesiger Kalendertag. toISOString() laege zwischen
// Mitternacht und 02:00 Uhr einen Tag daneben.
const alsTag = (d) => d.toLocaleDateString('sv-SE');

function heute() {
  return alsTag(new Date());
}
function jahresAnfang(offset = 0) {
  return `${new Date().getFullYear() + offset}-01-01`;
}
function jahresEnde(offset = 0) {
  return `${new Date().getFullYear() + offset}-12-31`;
}
function vorMonaten(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return alsTag(d);
}
function vorTagen(n) {
  return alsTag(new Date(Date.now() - n * 86400000));
}
function monatLabel(yyyymm) {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1)
    .toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

const SCHNELL = [
  { label: 'Dieses Jahr',      von: () => jahresAnfang(0), bis: () => jahresEnde(0) },
  { label: 'Letztes Jahr',     von: () => jahresAnfang(-1), bis: () => jahresEnde(-1) },
  { label: 'Letzte 12 Monate', von: () => vorMonaten(12),  bis: () => heute() },
  { label: 'Letzte 30 Tage',   von: () => vorTagen(30),    bis: () => heute() },
  { label: 'Letzte 7 Tage',    von: () => vorTagen(7),     bis: () => heute() },
];

export default function Statistik() {
  const { user } = useAuth();
  const istAdmin = ['admin', 'schwimmadmin'].includes(user?.rolle);
  const [tab, setTab] = useState('abzeichen'); // 'abzeichen' | 'anwesenheit' | 'auswertungen' | 'verguetung'

  const [von, setVon]       = useState(jahresAnfang(0));
  const [bis, setBis]       = useState(jahresEnde(0));
  const [vonInput, setVonInput] = useState(jahresAnfang(0));
  const [bisInput, setBisInput] = useState(jahresEnde(0));
  const [schnellAktiv, setSchnellAktiv] = useState(0); // Index in SCHNELL

  // ── Abzeichen-Daten ──────────────────────────────────────────────────────────
  const [daten, setDaten]   = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Anwesenheits-Daten ───────────────────────────────────────────────────────
  const [anwGruppen, setAnwGruppen] = useState([]);
  const [anwGesamt, setAnwGesamt]   = useState(null);
  const [anwLoading, setAnwLoading] = useState(true);

  // ── Auswertungen (Durchlaufzeit / Abbrecherquote / Wartezeit) ─────────────────
  const [auswertungen, setAuswertungen] = useState(null);
  const [ausLoading, setAusLoading]     = useState(true);

  // ── Vergütung: immer der aktuelle Stand, unabhängig vom gewählten Zeitraum ────
  const [verguetung, setVerguetung]     = useState(null);
  const [vergLoading, setVergLoading]   = useState(true);
  const [vergFehler, setVergFehler]     = useState('');

  const loadVerguetung = useCallback(async () => {
    setVergLoading(true); setVergFehler('');
    try {
      const r = await api.get('/api/admin/statistik/verguetung');
      setVerguetung(r.data);
    } catch (err) { setVergFehler(err.response?.data?.error || 'Vergütung konnte nicht geladen werden'); }
    finally { setVergLoading(false); }
  }, []);

  const loadAbzeichen = useCallback(async (vonD, bisD) => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/statistik/abzeichen', { params: { von: vonD, bis: bisD } });
      setDaten(r.data.daten || []);
      setGesamt(r.data.gesamt || 0);
    } catch { /* ignoriert */ }
    finally { setLoading(false); }
  }, []);

  const loadAnwesenheit = useCallback(async (vonD, bisD) => {
    setAnwLoading(true);
    try {
      const r = await api.get('/api/admin/statistik/anwesenheit', { params: { von: vonD, bis: bisD } });
      setAnwGruppen(r.data.gruppen || []);
      setAnwGesamt(r.data.gesamt || null);
    } catch { /* ignoriert */ }
    finally { setAnwLoading(false); }
  }, []);

  const loadAuswertungen = useCallback(async (vonD, bisD) => {
    setAusLoading(true);
    try {
      const r = await api.get('/api/admin/statistik/auswertungen', { params: { von: vonD, bis: bisD } });
      setAuswertungen(r.data);
    } catch { /* ignoriert */ }
    finally { setAusLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'abzeichen') loadAbzeichen(von, bis);
    else if (tab === 'anwesenheit') loadAnwesenheit(von, bis);
    else if (tab === 'verguetung') loadVerguetung();
    else loadAuswertungen(von, bis);
  }, [loadAbzeichen, loadAnwesenheit, loadAuswertungen, loadVerguetung, tab, von, bis]);

  const anwenden = () => {
    setVon(vonInput);
    setBis(bisInput);
    setSchnellAktiv(null);
  };

  const schnellWaehlen = (idx) => {
    const s = SCHNELL[idx];
    const v = s.von(), b = s.bis();
    setVon(v); setBis(b);
    setVonInput(v); setBisInput(b);
    setSchnellAktiv(idx);
  };

  // ── Abzeichen-Datenverarbeitung ────────────────────────────────────────────

  const nachKurs = {};
  KURS_REIHENFOLGE.forEach(k => { nachKurs[k] = 0; });
  daten.forEach(d => { nachKurs[d.kurs_typ] = (nachKurs[d.kurs_typ] || 0) + d.anzahl; });

  const trainer = [...new Set(daten.map(d => d.trainer))].sort((a, b) => {
    if (a === 'Kein Trainer') return 1;
    if (b === 'Kein Trainer') return -1;
    return a.localeCompare(b, 'de');
  });

  const matrix = {};
  trainer.forEach(t => { matrix[t] = {}; KURS_REIHENFOLGE.forEach(k => { matrix[t][k] = 0; }); });
  daten.forEach(d => {
    if (!matrix[d.trainer]) matrix[d.trainer] = {};
    matrix[d.trainer][d.kurs_typ] = (matrix[d.trainer][d.kurs_typ] || 0) + d.anzahl;
  });

  const trainerSumme = {};
  trainer.forEach(t => {
    trainerSumme[t] = KURS_REIHENFOLGE.reduce((s, k) => s + (matrix[t][k] || 0), 0);
  });

  const monate = [...new Set(daten.map(d => d.monat))].sort();
  const nachMonat = {};
  monate.forEach(m => {
    nachMonat[m] = { _gesamt: 0 };
    KURS_REIHENFOLGE.forEach(k => { nachMonat[m][k] = 0; });
  });
  daten.forEach(d => {
    if (!nachMonat[d.monat]) return;
    nachMonat[d.monat][d.kurs_typ] = (nachMonat[d.monat][d.kurs_typ] || 0) + d.anzahl;
    nachMonat[d.monat]._gesamt += d.anzahl;
  });
  const maxMonat = Math.max(1, ...monate.map(m => nachMonat[m]._gesamt));

  const kurseMitDaten = KURS_REIHENFOLGE.filter(k => nachKurs[k] > 0);

  // ── Render-Helfer: Quote-Farbe ───────────────────────────────────────────────
  function quoteFarbe(q) {
    if (q === null || q === undefined) return '#999';
    if (q >= 85) return '#2e7d32';
    if (q >= 65) return '#f57f17';
    return '#c62828';
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Statistik</h2>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', marginBottom: '1.25rem', border: '1px solid var(--border, #e0e0e0)', maxWidth: 560 }}>
        {[['abzeichen', '🏅 Abzeichen'], ['anwesenheit', '✓ Anwesenheit'], ['auswertungen', '📈 Auswertungen'],
          ...(istAdmin ? [['verguetung', '💶 Vergütung']] : [])].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              flex: 1, padding: '0.6rem', border: 'none', cursor: 'pointer',
              fontWeight: tab === key ? 700 : 400, fontSize: '0.88rem',
              background: tab === key ? 'var(--primary)' : '#fff',
              color: tab === key ? '#fff' : '#333',
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Zeitraum-Auswahl (die Vergütung zeigt immer den aktuellen Stand) ── */}
      <div className="card" style={{ marginBottom: '1.25rem', display: tab === 'verguetung' ? 'none' : undefined }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {SCHNELL.map((s, i) => (
            <button key={i}
              onClick={() => schnellWaehlen(i)}
              style={{
                padding: '0.35rem 0.875rem', borderRadius: 6, border: '1px solid',
                cursor: 'pointer', fontSize: '0.83rem', fontWeight: schnellAktiv === i ? 700 : 400,
                background: schnellAktiv === i ? 'var(--primary)' : 'transparent',
                color: schnellAktiv === i ? '#fff' : 'var(--primary)',
                borderColor: 'var(--primary)',
              }}>
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.75rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem', fontWeight: 600 }}>
            Von
            <input type="date" value={vonInput} onChange={e => { setVonInput(e.target.value); setSchnellAktiv(null); }}
              style={{ padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem', fontWeight: 600 }}>
            Bis
            <input type="date" value={bisInput} onChange={e => { setBisInput(e.target.value); setSchnellAktiv(null); }}
              style={{ padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.9rem' }} />
          </label>
          <button onClick={anwenden} className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end' }}>
            Anwenden
          </button>
        </div>
      </div>

      {/* ══════════════════════════ ABZEICHEN-TAB ══════════════════════════ */}
      {tab === 'abzeichen' && (
        loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : gesamt === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">🏅</div>
              <div className="empty-state-text">Keine abgeschlossenen Abzeichen im gewählten Zeitraum</div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Gesamt-Übersicht ──────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <div style={{
                background: 'var(--primary)', color: '#fff',
                borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800 }}>{gesamt}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 4 }}>Abzeichen gesamt</div>
              </div>

              {KURS_REIHENFOLGE.map(k => {
                const info = KURS_INFO[k];
                const n = nachKurs[k] || 0;
                return (
                  <div key={k} style={{
                    background: info.bg, border: `1px solid ${info.farbe}30`,
                    borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center',
                    opacity: n === 0 ? 0.4 : 1,
                  }}>
                    <div style={{ fontSize: '1.4rem' }}>{info.emoji}</div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: info.farbe }}>{n}</div>
                    <div style={{ fontSize: '0.75rem', color: info.farbe, marginTop: 2 }}>{info.label}</div>
                  </div>
                );
              })}
            </div>

            {/* ── Trainer-Matrix ────────────────────────────────────────────── */}
            {trainer.length > 0 && (
              <div className="card" style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 700 }}>
                  Abzeichen nach Trainer
                </h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Trainer</th>
                        {kurseMitDaten.map(k => (
                          <th key={k} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {KURS_INFO[k].emoji} {KURS_INFO[k].kurz}
                          </th>
                        ))}
                        <th style={{ textAlign: 'center', fontWeight: 800 }}>Gesamt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainer.map(t => (
                        <tr key={t}>
                          <td style={{ fontWeight: 600 }}>{t}</td>
                          {kurseMitDaten.map(k => {
                            const n = matrix[t]?.[k] || 0;
                            const info = KURS_INFO[k];
                            return (
                              <td key={k} style={{ textAlign: 'center' }}>
                                {n > 0 ? (
                                  <span style={{
                                    display: 'inline-block', minWidth: 28, padding: '2px 8px',
                                    borderRadius: 12, fontWeight: 700, fontSize: '0.82rem',
                                    background: info.bg, color: info.farbe,
                                  }}>
                                    {n}
                                  </span>
                                ) : (
                                  <span style={{ color: '#ccc' }}>–</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ textAlign: 'center', fontWeight: 800, fontSize: '1rem' }}>
                            {trainerSumme[t]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'var(--bg-alt, #f8f9fa)', fontWeight: 800 }}>
                        <td>Gesamt</td>
                        {kurseMitDaten.map(k => (
                          <td key={k} style={{ textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', minWidth: 28, padding: '2px 8px',
                              borderRadius: 12, fontWeight: 800, fontSize: '0.82rem',
                              background: KURS_INFO[k].bg, color: KURS_INFO[k].farbe,
                            }}>
                              {nachKurs[k]}
                            </span>
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', fontSize: '1rem', fontWeight: 800 }}>{gesamt}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* ── Monatsübersicht ───────────────────────────────────────────── */}
            {monate.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 700 }}>
                  Monatsübersicht
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {monate.map(m => {
                    const md = nachMonat[m];
                    const breite = (md._gesamt / maxMonat) * 100;
                    return (
                      <div key={m} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ minWidth: 90, fontSize: '0.83rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {monatLabel(m)}
                        </div>
                        <div style={{ flex: 1, height: 22, background: '#f0f4f8', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                          <div style={{ display: 'flex', height: '100%', width: `${breite}%`, transition: 'width 0.3s' }}>
                            {KURS_REIHENFOLGE.filter(k => md[k] > 0).map(k => (
                              <div key={k} style={{
                                flex: md[k],
                                background: KURS_INFO[k].farbe,
                                opacity: 0.85,
                                transition: 'flex 0.3s',
                              }}
                                title={`${KURS_INFO[k].label}: ${md[k]}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div style={{ minWidth: 28, textAlign: 'right', fontWeight: 700, fontSize: '0.9rem' }}>
                          {md._gesamt}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', minWidth: 180 }}>
                          {KURS_REIHENFOLGE.filter(k => md[k] > 0).map(k => {
                            const info = KURS_INFO[k];
                            return (
                              <span key={k} style={{
                                padding: '1px 6px', borderRadius: 10, fontSize: '0.72rem',
                                background: info.bg, color: info.farbe, fontWeight: 700,
                                whiteSpace: 'nowrap',
                              }}>
                                {info.emoji} {md[k]}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1.25rem', paddingTop: '0.875rem', borderTop: '1px solid #f0f0f0' }}>
                  {kurseMitDaten.map(k => {
                    const info = KURS_INFO[k];
                    return (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                        <div style={{ width: 14, height: 14, borderRadius: 3, background: info.farbe }} />
                        <span style={{ color: 'var(--text-muted)' }}>{info.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* ══════════════════════════ ANWESENHEITS-TAB ══════════════════════════ */}
      {tab === 'anwesenheit' && (
        anwLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : !anwGesamt || anwGesamt.gesamt === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">✓</div>
              <div className="empty-state-text">Keine erfassten Anwesenheiten im gewählten Zeitraum</div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Gesamt-Übersicht ──────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <div style={{
                background: 'var(--primary)', color: '#fff',
                borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 800 }}>{anwGesamt.quote ?? '–'}{anwGesamt.quote !== null && '%'}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 4 }}>Anwesenheitsquote</div>
              </div>
              <div style={{ background: '#e8f5e9', border: '1px solid #2e7d3230', borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#2e7d32' }}>{anwGesamt.anwesend}</div>
                <div style={{ fontSize: '0.75rem', color: '#2e7d32', marginTop: 2 }}>Anwesend</div>
              </div>
              <div style={{ background: '#fff8e1', border: '1px solid #f57f1730', borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f57f17' }}>{anwGesamt.entschuldigt}</div>
                <div style={{ fontSize: '0.75rem', color: '#f57f17', marginTop: 2 }}>Entschuldigt</div>
              </div>
              <div style={{ background: '#ffebee', border: '1px solid #c6282830', borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#c62828' }}>{anwGesamt.unentschuldigt}</div>
                <div style={{ fontSize: '0.75rem', color: '#c62828', marginTop: 2 }}>Unentschuldigt</div>
              </div>
            </div>

            {/* ── Pro Gruppe ────────────────────────────────────────────────── */}
            <div className="card">
              <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 700 }}>
                Anwesenheit nach Gruppe
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                {anwGruppen.map(g => {
                  const info = KURS_INFO[g.kurs_typ] || { emoji: '🏊', farbe: '#555' };
                  const farbe = quoteFarbe(g.quote);
                  return (
                    <div key={g.gruppe_id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
                        <span>{info.emoji}</span>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{g.name}</span>
                        <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: '0.95rem', color: farbe }}>
                          {g.quote ?? '–'}{g.quote !== null && '%'}
                        </span>
                      </div>
                      <div style={{ height: 16, background: '#f0f4f8', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${g.quote ?? 0}%`, height: '100%', background: farbe, transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        <span>Anwesend: <strong style={{ color: '#2e7d32' }}>{g.anwesend}</strong></span>
                        <span>Entschuldigt: <strong style={{ color: '#f57f17' }}>{g.entschuldigt}</strong></span>
                        <span>Unentschuldigt: <strong style={{ color: '#c62828' }}>{g.unentschuldigt}</strong></span>
                        <span style={{ marginLeft: 'auto' }}>{g.gesamt} erfasst</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )
      )}

      {/* ══════════════════════════ VERGÜTUNGS-TAB ══════════════════════════ */}
      {tab === 'verguetung' && istAdmin && (
        vergLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : vergFehler || !verguetung ? (
          <div className="alert alert-error">{vergFehler || 'Keine Daten'}</div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>
                Laufendes Quartal – Q{verguetung.quartal.quartal} {verguetung.quartal.jahr}
              </h3>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                {fmtDatum(verguetung.quartal.von)} – {fmtDatum(verguetung.quartal.bis)} ·
                Stand {fmtDatum(verguetung.heute)} ·
                {verguetung.quartal.abgeschlossen ? ' festgeschrieben' : ' vorläufig, wächst mit jedem Training'}
              </p>
              <div className="stat-grid" style={{ marginBottom: '1rem' }}>
                <div className="stat-card"><div className="stat-label">Stunden</div>
                  <div className="stat-value">{fmtZahl(verguetung.quartal.gesamt.stunden)}</div></div>
                <div className="stat-card"><div className="stat-label">Auszuzahlen</div>
                  <div className="stat-value">{fmtEuro(verguetung.quartal.gesamt.betrag)}</div></div>
                <div className="stat-card"><div className="stat-label">Personen</div>
                  <div className="stat-value">{verguetung.quartal.gesamt.personen}</div></div>
                <div className="stat-card"><div className="stat-label">Einsätze</div>
                  <div className="stat-value">{verguetung.quartal.gesamt.einsaetze}</div></div>
              </div>
              {verguetung.quartal.gesamt.einsaetze_ohne_satz > 0 && (
                <div className="alert" style={{ background: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' }}>
                  {fmtZahl(verguetung.quartal.gesamt.stunden_ohne_satz)} Stunden ohne hinterlegten Stundensatz
                  ({verguetung.quartal.gesamt.rollen_ohne_satz.join(', ')}) – sie zählen mit 0,00 €.
                </div>
              )}
              {verguetung.quartal.personen.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  In diesem Quartal wurden noch keine Stunden erfasst
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Person</th><th>Rolle</th><th>Stunden</th><th>Zusammensetzung</th><th>Auszuzahlen</th></tr></thead>
                    <tbody>
                      {verguetung.quartal.personen.map(p => (
                        <tr key={p.mitglied_id}>
                          <td style={{ fontWeight: 600 }}>{p.nachname}, {p.vorname}</td>
                          <td>{p.rolle}</td>
                          <td>{fmtZahl(p.stunden)}</td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {(p.posten || []).map(o => `${fmtZahl(o.stunden)} × ${o.satz === null ? 'kein Satz' : fmtEuro(o.satz)}`).join(' · ')}
                          </td>
                          <td style={{ fontWeight: 700 }}>{fmtEuro(p.betrag)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {verguetung.leistungsgruppen?.gruppen?.length > 0 && (
              <div className="card" style={{ marginBottom: '1.25rem' }}>
                <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>
                  Leistungsgruppen im laufenden Quartal
                </h3>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Nur Gruppenanwesenheit – keine Namen. Die Stunden stecken in den Beträgen oben mit drin.
                </p>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Gruppe</th><th>Trainer</th><th>Trainings</th><th>Schwimmer ⌀</th><th>Betreuende</th><th>Stunden</th></tr></thead>
                    <tbody>
                      {verguetung.leistungsgruppen.gruppen.map(g => (
                        <tr key={g.id} style={{ opacity: g.aktiv ? 1 : 0.6 }}>
                          <td style={{ fontWeight: 600 }}>{g.name}</td>
                          <td style={{ fontSize: '0.85rem' }}>{g.trainer_name || '–'}</td>
                          <td>{g.trainings}</td>
                          <td>{g.schnitt_teilnehmer === null ? '–' : fmtZahl(g.schnitt_teilnehmer)}</td>
                          <td>{g.personen}</td>
                          <td style={{ fontWeight: 700 }}>{fmtZahl(g.stunden)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="card">
              <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>Jahr {verguetung.jahr.jahr}</h3>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Abgeschlossene Quartale mit ihren festgeschriebenen Zahlen, das laufende Quartal tagesaktuell.
              </p>
              <div className="table-wrap" style={{ marginBottom: '1.25rem' }}>
                <table>
                  <thead><tr><th>Quartal</th><th>Zeitraum</th><th>Stand</th><th>Stunden</th><th>Betrag</th></tr></thead>
                  <tbody>
                    {verguetung.jahr.quartale.map(q => (
                      <tr key={q.quartal}>
                        <td style={{ fontWeight: 600 }}>Q{q.quartal}</td>
                        <td style={{ fontSize: '0.82rem' }}>{fmtDatum(q.von)} – {fmtDatum(q.bis)}</td>
                        <td style={{ fontSize: '0.82rem' }}>{q.abgeschlossen ? '🔒 abgeschlossen' : 'vorläufig'}</td>
                        <td>{fmtZahl(q.stunden)}</td>
                        <td style={{ fontWeight: 700 }}>{fmtEuro(q.betrag)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid var(--border, #e0e0e0)' }}>
                      <td colSpan={3} style={{ fontWeight: 700 }}>Summe {verguetung.jahr.jahr}</td>
                      <td style={{ fontWeight: 700 }}>{fmtZahl(verguetung.jahr.gesamt.stunden)}</td>
                      <td style={{ fontWeight: 700 }}>{fmtEuro(verguetung.jahr.gesamt.betrag)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '0.5rem' }}>Je Person im Jahr</h4>
              {verguetung.jahr.personen.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  In diesem Jahr wurden noch keine Stunden erfasst
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Person</th><th>Rolle</th><th>Einsätze</th><th>Stunden</th><th>Betrag im Jahr</th></tr></thead>
                    <tbody>
                      {verguetung.jahr.personen.map(p => (
                        <tr key={p.mitglied_id}>
                          <td style={{ fontWeight: 600 }}>{p.nachname}, {p.vorname}</td>
                          <td>{p.rolle}</td>
                          <td>{p.einsaetze}</td>
                          <td>{fmtZahl(p.stunden)}</td>
                          <td style={{ fontWeight: 700 }}>{fmtEuro(p.betrag)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )
      )}

      {/* ══════════════════════════ AUSWERTUNGEN-TAB ══════════════════════════ */}
      {tab === 'auswertungen' && (
        ausLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : !auswertungen ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">📈</div>
              <div className="empty-state-text">Auswertungen konnten nicht geladen werden</div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Durchlaufzeit pro Ausbildungsstufe ───────────────────────────── */}
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>
                Durchlaufzeit pro Ausbildungsstufe
              </h3>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Zeitspanne zwischen Eintritt und Abzeichen-Erhalt in einer Gruppe (nur abgeschlossene Aufenthalte mit erhaltenem Abzeichen)
              </p>
              {auswertungen.durchlaufzeit.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Keine abgeschlossenen Aufenthalte mit Abzeichen im gewählten Zeitraum
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Ausbildungsstufe</th>
                        <th style={{ textAlign: 'center' }}>Anzahl</th>
                        <th style={{ textAlign: 'center' }}>⌀ Durchlaufzeit</th>
                        <th style={{ textAlign: 'center' }}>Median</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auswertungen.durchlaufzeit.map(d => {
                        const info = KURS_INFO[d.kurs_typ] || { label: d.kurs_typ, farbe: '#555', bg: '#f5f5f5', emoji: '🏊' };
                        return (
                          <tr key={d.kurs_typ}>
                            <td>
                              <span style={{
                                padding: '2px 9px', borderRadius: 12, fontWeight: 700, fontSize: '0.82rem',
                                background: info.bg, color: info.farbe,
                              }}>
                                {info.emoji} {info.label}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{d.anzahl}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700 }}>
                              {d.durchschnitt_tage !== null ? `${d.durchschnitt_tage} Tage` : '–'}
                            </td>
                            <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                              {d.median_tage !== null ? `${d.median_tage} Tage` : '–'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Abbrecherquote ────────────────────────────────────────────────── */}
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>
                Abbrecherquote
              </h3>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Anteil der Vereinsaustritte ohne erhaltenes Abzeichen, relativ zu allen abgeschlossenen Aufenthalten
              </p>
              {auswertungen.abbrecherquote.gesamt.gesamt === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Keine abgeschlossenen Aufenthalte im gewählten Zeitraum
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'inline-block', marginBottom: '1rem', padding: '0.875rem 1.5rem',
                    background: '#f0f4f8', borderRadius: 10, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: quoteFarbe(100 - (auswertungen.abbrecherquote.gesamt.quote ?? 0)) }}>
                      {auswertungen.abbrecherquote.gesamt.quote ?? '–'}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Gesamt ({auswertungen.abbrecherquote.gesamt.abbrecher} von {auswertungen.abbrecherquote.gesamt.gesamt})
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                    {auswertungen.abbrecherquote.pro_kurs.map(k => {
                      const info = KURS_INFO[k.kurs_typ] || { label: k.kurs_typ, farbe: '#555', emoji: '🏊' };
                      const farbe = quoteFarbe(100 - (k.quote ?? 0));
                      return (
                        <div key={k.kurs_typ}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem' }}>
                            <span>{info.emoji}</span>
                            <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{info.label}</span>
                            <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: '0.9rem', color: farbe }}>
                              {k.quote ?? '–'}{k.quote !== null && '%'}
                            </span>
                          </div>
                          <div style={{ height: 12, background: '#f0f4f8', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${k.quote ?? 0}%`, height: '100%', background: farbe, transition: 'width 0.3s' }} />
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                            {k.abbrecher} von {k.gesamt} Aufenthalten
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* ── Wartezeit-Entwicklung ─────────────────────────────────────────── */}
            <div className="card">
              <h3 style={{ marginBottom: '0.25rem', fontSize: '1rem', fontWeight: 700 }}>
                Wartezeit-Entwicklung
              </h3>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Durchschnittliche Wartezeit bis zur Aufnahme, nach Aufnahme-Monat. Erfasst nur Schwimmer, die ab Einführung
                dieser Auswertung aus der Warteliste aufgenommen wurden – für Altbestand liegen keine Daten vor.
              </p>
              {auswertungen.wartezeit_entwicklung.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Noch keine auswertbaren Wartezeiten im gewählten Zeitraum
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {(() => {
                    const maxTage = Math.max(1, ...auswertungen.wartezeit_entwicklung.map(w => w.durchschnitt_tage || 0));
                    return auswertungen.wartezeit_entwicklung.map(w => (
                      <div key={w.monat} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ minWidth: 90, fontSize: '0.83rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {monatLabel(w.monat)}
                        </div>
                        <div style={{ flex: 1, height: 18, background: '#f0f4f8', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            width: `${((w.durchschnitt_tage || 0) / maxTage) * 100}%`, height: '100%',
                            background: 'var(--primary)', opacity: 0.8, transition: 'width 0.3s',
                          }} />
                        </div>
                        <div style={{ minWidth: 70, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem' }}>
                          {w.durchschnitt_tage !== null ? `${w.durchschnitt_tage} T.` : '–'}
                        </div>
                        <div style={{ minWidth: 60, textAlign: 'right', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {w.anzahl} Kind{w.anzahl === 1 ? '' : 'er'}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </>
        )
      )}
    </div>
  );
}
